import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Line from "../Utilities/Line";
import { useContext, useEffect, useState, useMemo } from "react";
import { APIContext } from "../Contexts/APIContext";
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import { Box } from "@mui/material";
import { TelemetryContext } from "../Contexts/TelemetryContext";

export interface IProps {
    title: string;
    rootPath: string;
    container: string;
    legacy?: boolean;
}

export interface DirectoryItem {
    checked: boolean;
    name: string;
    downloadable: boolean;
    url: string;
    createdDateTime: Date | null;
}

const fmtDate = (d?: Date | string | null) =>
    !d
        ? ""
        : new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,     // 24h format, remove if you want AM/PM
            timeZone: "Europe/London",
        }).format(new Date(d));


const APP_HEADER_H = 100;

export default function FileExplorer(props: IProps) {
    const { get, post, getPDF } = useContext(APIContext);
    const { logFileAccess } = useContext(TelemetryContext);
    const [fileNames, setFileNames] = useState<DirectoryItem[]>([]);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [currentPath, setCurrentPath] = useState(props.rootPath);
    const navigate = useNavigate();


    useEffect(() => {
        updateList();
    }, [currentPath]);

    const deleteSelected = () => {
        if (!props.legacy) {
            post(`MyFiles`, `RemoveMyFiles?containerName=${props.container}&folderName=${currentPath}`, fileNames.filter((file) => file.checked), (data: boolean) => {
                if (data) {
                    setOpenDialog(false);
                    updateList();
                }
            });
        }
    }

    const updateList = () => {
        get(`MyFiles`, `GetMyFiles?container=${props.container}&folderName=${currentPath}`, setFileNames)
    }

    const updateSelected = (index: number, item: DirectoryItem) => {
        item.checked = !item.checked;
        let newList: DirectoryItem[] = [...fileNames];
        newList[index] = item;
        setFileNames(newList);
    }

    const goUp = () => {
        if (currentPath === (props.rootPath)) return;
        const parts = currentPath.split('/').filter(Boolean);
        parts.pop(); // remove last segment
        setCurrentPath('/' + parts.join('/') + (parts.length > 0 ? '/' : ''));
    };

    const handleClick = (entry: DirectoryItem) => {
        if (!entry.downloadable) {
            setCurrentPath(currentPath + entry.name + '/');
        }
        else {
            if (props.title === 'My Files') {
                logFileAccess(entry.name, props.title);
            }
            window.open(entry.url ?? '', "_blank");
        }
    };

    const openInfoFile = () => {
        getPDF(
            'Document',
            `GetLibraryFile?folder=SavedDocumentsInfoFolder&fileName=Note on Saving and Retrieving Work in TRICS`,
            `Note on Saving and Retriving Work in TRICS`,
            true
        );
    };

    const selectedCount = useMemo(() => fileNames.filter((f) => f.checked).length, [fileNames]);
    const cols = props.legacy ? 1 : 3;
    return (
        <Box sx={{ height: `calc(100svh - ${APP_HEADER_H}px)`, display: 'flex', flexDirection: 'column' }}>
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Typography variant="h6" textAlign="center" sx={{ mb: 2 }}>
                        Permanently delete {selectedCount} file(s)?
                    </Typography>
                    <Stack sx={{ maxHeight: 240, overflow: "auto", mb: 2 }}>
                        {fileNames
                            .filter((f) => f.checked)
                            .map((f) => (
                                <Typography key={f.name} variant="body2" noWrap title={f.name}>
                                    - {f.name}
                                </Typography>
                            ))}
                        {selectedCount === 0 && <Typography variant="body2">No files selected.</Typography>}
                    </Stack>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Button onClick={() => setOpenDialog(false)} variant="outlined" fullWidth>
                                Cancel
                            </Button>
                        </Grid>
                        <Grid size={6}>
                            <Button onClick={deleteSelected} variant="contained" fullWidth disabled={selectedCount === 0}>
                                Confirm
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            <Grid container>
                <Grid size={3}>
                    <Typography variant="h6">{props.title}</Typography>
                </Grid>
                <Grid size={1}>
                    <Button onClick={openInfoFile}>Info</Button>
                </Grid>
                {props.title === 'My Files' &&
                    <Grid size={5}>
                        <Button variant="contained" onClick={() => navigate("/legacyfiles")} sx={{ textTransform: 'none', letterSpacing: 'normal' }}>
                            Recover legacy sessions & reports
                        </Button>
                    </Grid>
                }
                {!props.legacy && (
                    <Grid size={3}>
                        <Button variant="contained" disabled={selectedCount === 0} onClick={() => setOpenDialog(true)}>
                            Delete Selected ({selectedCount})
                        </Button>
                    </Grid>
                )}
                <Line />
            </Grid>
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {!props.legacy && <TableCell width={48} />}   {/* checkbox col */}
                            <TableCell>Name</TableCell>
                            {!props.legacy && <TableCell>Created</TableCell>}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {currentPath !== props.rootPath && (
                            <TableRow hover onClick={goUp} style={{ cursor: 'pointer' }}>
                                <TableCell colSpan={cols}>
                                    <IconButton size="small"><ArrowUpward /></IconButton>
                                    (Back)
                                </TableCell>
                            </TableRow>
                        )}
                        {fileNames.map((entry, idx) => {
                            const isFolder = entry.downloadable === false;
                            return (
                                <TableRow key={idx} hover style={{ cursor: 'pointer' }}>
                                    {!props.legacy && (
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {!isFolder && (
                                                <Checkbox
                                                    checked={entry.checked}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        updateSelected(idx, entry);
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell onClick={() => handleClick(entry)}>
                                        {isFolder ? (
                                            <FolderOutlined sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        ) : (
                                            <InsertDriveFileOutlined sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        )}
                                        {entry.name}
                                    </TableCell>

                                    {!props.legacy && (
                                        <TableCell onClick={() => !isFolder && handleClick(entry)}>
                                            {!isFolder && entry.createdDateTime ? fmtDate(entry.createdDateTime) : ""}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

        </Box >
    );
}
